/**
 * The daily trading process, as a linear flow of steps grouped into three
 * phases. This is the single source of truth for both the /flow command center
 * and the cross-page Flow rail, so the two can never disagree on order or names.
 *
 * Each step points at a real page; some carry a `live` key naming a cheap metric
 * the command center surfaces on the card, and optional `more` quick-links.
 */
export type FlowTone = "accent" | "violet" | "bull";

export type FlowPhase = {
  id: string;
  n: number;
  name: string;
  tagline: string;
  tone: FlowTone;
};

export type FlowLive = "watchlists" | "openTrades" | "scans";

export type FlowStep = {
  id: string;
  n: number; // 1..N global order
  phase: string; // FlowPhase.id
  title: string;
  blurb: string;
  href: string;
  icon: string; // key into the icon map (components/flow/icons)
  more?: ReadonlyArray<{ label: string; href: string }>;
  live?: FlowLive;
};

export const FLOW_PHASES: ReadonlyArray<FlowPhase> = [
  { id: "assess", n: 1, name: "Assess the market", tagline: "Read the tape before you touch a trade.", tone: "accent" },
  { id: "decide", n: 2, name: "Decide & find ideas", tagline: "Turn the read into a shortlist.", tone: "violet" },
  { id: "execute", n: 3, name: "Execute & manage", tagline: "Pull the trigger, then manage the book.", tone: "bull" },
];

export const FLOW_STEPS: ReadonlyArray<FlowStep> = [
  // ── Assess ── the full market read, page by page.
  { id: "dashboard", n: 1, phase: "assess", title: "Dashboard", blurb: "A glance at what's moving right now.", href: "/dashboard", icon: "grid" },
  { id: "daily-brief", n: 2, phase: "assess", title: "Daily Brief", blurb: "The morning read \u2014 breadth, leaders and the day's setup.", href: "/daily-brief", icon: "sun" },
  { id: "brief", n: 3, phase: "assess", title: "Timing Brief", blurb: "Assess the market stage \u2014 where are we in the cycle?", href: "/timing/timing-brief", icon: "clipboard" },
  { id: "buckets", n: 4, phase: "assess", title: "Buckets Brief", blurb: "How the market splits across strength buckets.", href: "/timing/buckets", icon: "layers" },
  { id: "breadth-1", n: 5, phase: "assess", title: "Market Breadth 1", blurb: "Advance/decline and new highs/lows \u2014 the breadth backdrop.", href: "/timing/breadth-1", icon: "pulse" },
  { id: "breadth-2", n: 6, phase: "assess", title: "Market Breadth 2", blurb: "Per-index breadth signals across the majors.", href: "/timing/breadth-2", icon: "bars" },
  { id: "breadth-3", n: 7, phase: "assess", title: "Market Breadth 3", blurb: "% above the 20/50/200, 13-week highs and lows, the A/D line and RSI \u2014 per index.", href: "/timing/breadth-3", icon: "pulse" },
  { id: "us-etfs", n: 8, phase: "assess", title: "US Index ETFs", blurb: "Trend and momentum across the US index ETFs.", href: "/timing/index-etfs", icon: "candles" },
  { id: "commodities-etfs", n: 9, phase: "assess", title: "Commodities ETFs", blurb: "Where money's flowing across commodity ETFs.", href: "/timing/commodities-etfs", icon: "droplet" },
  { id: "guppy", n: 10, phase: "assess", title: "Guppy Charts", blurb: "Multiple-MA ribbons \u2014 trend health at a glance.", href: "/timing/guppy", icon: "waves" },
  { id: "ratio", n: 11, phase: "assess", title: "Ratio Charts", blurb: "Relative strength \u2014 what's leading, what's lagging.", href: "/timing/ratio", icon: "ratio" },
  { id: "macro", n: 12, phase: "assess", title: "Macro", blurb: "Yield curve, credit and sentiment \u2014 the backdrop behind the tape.", href: "/timing/macro", icon: "globe" },
  { id: "outlook", n: 13, phase: "assess", title: "Market Outlook", blurb: "The nightly read on the tape \u2014 and your own journal.", href: "/outlook", icon: "sparkle" },

  { id: "scanner", n: 14, phase: "decide", title: "Set direction · Scan", blurb: "Pick a bias, then scan for setups that fit the tape.", href: "/scanner", icon: "search", live: "scans" },
  { id: "candlesticks", n: 15, phase: "decide", title: "Candlesticks", blurb: "Stocks that made a candlestick pattern, and how often that pattern led to a move its way.", href: "/candlesticks", icon: "hammer" },
  { id: "minervini", n: 16, phase: "decide", title: "Minervini", blurb: "Stocks in an uptrend leaving a tight base, read by Minervini's rules.", href: "/minervini", icon: "trend" },
  { id: "chart", n: 17, phase: "decide", title: "Chart the ideas", blurb: "Pull up candidates, mark levels, build the list.", href: "/charts", icon: "candles", live: "watchlists" },
  { id: "confirm", n: 18, phase: "decide", title: "Confirm the edge", blurb: "The VPA volume story — confirm before you commit.", href: "/vpa", icon: "volume" },
  { id: "seasonality", n: 19, phase: "decide", title: "Seasonality", blurb: "The seasonal odds — is the calendar on your side?", href: "/seasonality", icon: "calendar" },
  { id: "options-flow", n: 20, phase: "decide", title: "Options flow", blurb: "Scan the unusual options activity tape for fresh ideas.", href: "/options?tab=flow", icon: "flame" },
  // ── Execute ──
  { id: "list", n: 21, phase: "execute", title: "Lock the trade list", blurb: "Finalize the names you'll actually trade.", href: "/charts", icon: "list", live: "watchlists" },
  { id: "options", n: 22, phase: "execute", title: "Options", blurb: "Express the same idea as an options structure.", href: "/options", icon: "sliders" },
  { id: "trade", n: 23, phase: "execute", title: "Execute", blurb: "Place the trade and log the entry.", href: "/wealth/trades", icon: "bolt" },
  { id: "manage", n: 24, phase: "execute", title: "Trade Management", blurb: "Manage open positions and update the journal.", href: "/wealth/trades", icon: "briefcase", live: "openTrades" },
];

export function phaseOf(step: FlowStep): FlowPhase {
  return FLOW_PHASES.find((p) => p.id === step.phase) ?? FLOW_PHASES[0]!;
}
export function stepsOfPhase(phaseId: string): FlowStep[] {
  return FLOW_STEPS.filter((s) => s.phase === phaseId);
}
export function stepById(id: string | null): FlowStep | null {
  return id ? FLOW_STEPS.find((s) => s.id === id) ?? null : null;
}

/**
 * Tailwind class fragments per tone, so page + rail render identically. Every
 * value is a COMPLETE, literal class string (incl. the `hover:` variants) so
 * Tailwind's static scanner emits them — never build these by concatenation.
 */
export const TONE: Record<FlowTone, { text: string; softBg: string; border: string; hoverBorder: string; ring: string; dot: string }> = {
  accent: { text: "text-accent", softBg: "bg-accent-soft", border: "border-accent", hoverBorder: "hover:border-accent", ring: "ring-accent/40", dot: "bg-accent" },
  violet: { text: "text-accent", softBg: "bg-accent-soft", border: "border-accent", hoverBorder: "hover:border-accent", ring: "ring-accent/40", dot: "bg-accent" },
  bull: { text: "text-bull", softBg: "bg-bull-soft", border: "border-bull", hoverBorder: "hover:border-bull", ring: "ring-bull/40", dot: "bg-bull" },
};
